import type { Tone } from "../hub/ui.tsx"
import type {
  Channel,
  ChannelState,
  ContactType
} from "../types/compose.types.ts"
import type { ContactConsent } from "../types/consent.types.ts"

export interface ConsentChip {
  tone: Tone
  /** Under `recordComm.status`. */
  key: string
}

export interface ChannelSummary {
  address: string
  /** Whether a service (transactional) message may go: the fact a sender acts on. */
  service: ConsentChip
  /** Marketing on its own line; `null` where it does not apply. */
  marketing: ConsentChip | null
  /** Why a message on this channel cannot go to this contact; `null` when it can. */
  blocked: string | null
}

const BUSINESS: ContactType[] = ["subcontractor", "supplier"]

const doNotContact = (consent: ContactConsent) =>
  !!(consent as ContactConsent & { do_not_contact?: boolean }).do_not_contact

const blockedReason = (
  channel: Channel,
  state: ChannelState,
  dnc: boolean
): string | null => {
  if (channel === "sms" && !state.available) return "smsNotSetUp"
  if (!state.address) return channel === "sms" ? "noPhone" : "noEmail"
  if (dnc) return "doNotContact"
  if (state.suppressed) return "blocked"
  if (channel === "sms" && state.state === "stopped") return "optedOutTexts"
  if (!state.can_send?.transactional?.allowed)
    return channel === "sms" ? "textsNotAllowed" : "emailNotAllowed"
  return null
}

const SERVICE_ALLOWED: Record<Channel, string> = {
  email: "serviceEmailAllowed",
  sms: "serviceTextsAllowed"
}

const marketingChip = (channel: Channel, state: ChannelState): ConsentChip => {
  if (channel === "email")
    return state.state === "unsubscribed"
      ? { tone: "warning", key: "unsubscribedMarketing" }
      : { tone: "success", key: "marketingEmailAllowed" }
  if (state.marketing_opt_in && state.can_send?.marketing?.allowed)
    return { tone: "success", key: "marketingTextsConsented" }
  if (state.marketing_requested_at)
    return { tone: "warning", key: "marketingConsentRequested" }
  return { tone: "warning", key: "marketingConsentMissing" }
}

/**
 * What one channel means for one contact, read from the server's own
 * decision (`can_send`) and standing; an address on file is never taken as
 * consent.
 */
export const summarizeChannel = (
  consent: ContactConsent,
  channel: Channel
): ChannelSummary => {
  const state = consent.channels[channel]
  const blocked = blockedReason(channel, state, doNotContact(consent))
  const business = BUSINESS.includes(consent.contact_type as ContactType)
  return {
    address: state.address,
    service: blocked
      ? {
          tone:
            blocked === "noEmail" ||
            blocked === "noPhone" ||
            blocked === "smsNotSetUp"
              ? "neutral"
              : "danger",
          key: blocked
        }
      : { tone: "success", key: SERVICE_ALLOWED[channel] },
    marketing: business || blocked ? null : marketingChip(channel, state),
    blocked
  }
}
